import { ValidationError, ValidationResult, SampleOrderFormData } from '../types';

/**
 * Validates an email address format
 */
export function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;
  const trimmed = email.trim();
  if (trimmed.length > 254) return false;
  if (trimmed.includes('..')) return false;
  // Requires a real-looking local part, a domain with at least one dot, and a TLD of 2+ letters
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(trimmed);
}

/**
 * Validates a phone number format, requiring an international country code.
 * Roasters submitting sample requests are worldwide, so a bare local number
 * (e.g. "555 1234") is ambiguous — we require the leading "+" + country code
 * (E.164-style: 8-15 digits total after the "+").
 */
export function validatePhone(phone: string): boolean {
  if (!phone || phone.trim() === '') return false;
  const trimmed = phone.trim();
  if (!trimmed.startsWith('+')) return false;
  // Allow digits plus spaces/hyphens/parens for readable grouping, e.g. "+1 (555) 123-4567"
  const phoneRegex = /^\+[\d\s\-\(\)]+$/;
  if (!phoneRegex.test(trimmed)) return false;
  const digitsOnly = trimmed.replace(/\D/g, '');
  return digitsOnly.length >= 8 && digitsOnly.length <= 15;
}

/**
 * Validates that a required field is not empty
 */
export function validateRequired(value: string): boolean {
  return value !== null && value !== undefined && value.trim() !== '';
}

/**
 * Rejects messages that are too short or contain no actual text (e.g. a bare
 * phone number stuffed into the field) — a pattern seen from spam bots.
 */
export function validateMessageQuality(message: string): boolean {
  const trimmed = (message || '').trim();
  if (trimmed.length < 10) return false;
  if (/^[\d\s\-\+\(\)]+$/.test(trimmed)) return false;
  return true;
}

/**
 * Validates that an array is not empty
 */
export function validateArrayNotEmpty<T>(arr: T[]): boolean {
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * Validates the complete sample order form data
 */
export function validateSampleOrderForm(formData: SampleOrderFormData): ValidationResult {
  const errors: ValidationError[] = [];

  // Validate name
  if (!validateRequired(formData.name)) {
    errors.push({ field: 'name', message: 'Name is required' });
  }

  // Validate email
  if (!validateRequired(formData.email)) {
    errors.push({ field: 'email', message: 'Email is required' });
  } else if (!validateEmail(formData.email)) {
    errors.push({ field: 'email', message: 'Please enter a valid email address' });
  }

  // Validate company
  if (!validateRequired(formData.company)) {
    errors.push({ field: 'company', message: 'Company is required' });
  }

  // Validate phone
  if (!validateRequired(formData.phone)) {
    errors.push({ field: 'phone', message: 'Phone number is required' });
  } else if (!validatePhone(formData.phone)) {
    errors.push({ field: 'phone', message: 'Include your country code, e.g. +1 555 123 4567' });
  }

  // Validate shipping address — used to determine free (EU/UK) vs $60 DHL
  // shipping and must be confirmed before samples can be dispatched.
  if (!validateRequired(formData.country)) {
    errors.push({ field: 'country', message: 'Country is required' });
  }
  if (!validateRequired(formData.addressLine1)) {
    errors.push({ field: 'addressLine1', message: 'Street address is required' });
  }
  if (!validateRequired(formData.city)) {
    errors.push({ field: 'city', message: 'City is required' });
  }
  if (!validateRequired(formData.postalCode)) {
    errors.push({ field: 'postalCode', message: 'Postal code is required' });
  }

  // Validate selected coffees
  if (!validateArrayNotEmpty(formData.selectedCoffees)) {
    errors.push({ field: 'selectedCoffees', message: 'Please select at least one coffee' });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
